<template>
  <div v-if="visible" class="panel-header" :class="{ 'no-title': !hasTitle, 'no-bottom-margin': noBottomMargin }">
    <div class="panel-left" :class="{ 'no-title': !hasTitle }">
      <slot name="back_button_action"></slot>
      <h2 class="panel-title">{{ title }}</h2>
      <div class="panel-actions">
        <button v-if="showFilter" class="icon-btn filter-btn" type="button" :title="$t('common.filter')" @click="handleFilter">
          <img :src="filterIcon" alt="Filter" />
        </button>
        <button v-if="showAdd" class="icon-btn primary" type="button" :title="$t('common.add')" @click="handleAdd">
          <img :src="plusIcon" alt="Add" />
        </button>
      </div>
    </div>
    <div class="panel-actions right" :class="{ 'has-actions-slot': hasActionsSlot }">
      <button v-if="showRefresh" class="icon-btn" type="button" :title="$t('common.refresh')" @click="handleRefresh">
        <img :src="refreshIcon" alt="Refresh" />
      </button>
      <DxDropDownButton
        v-if="showExport && hasDownloadOptions"
        class="export-dropdown-btn"
        :items="downloadOptions"
        display-expr="text"
        key-expr="id"
        icon="download"
        :use-select-mode="false"
        :drop-down-options="{ width: 260 }"
        @item-click="handleExportOptionClick"
      />
      <button v-else-if="showExport" class="icon-btn" type="button" :title="$t('common.export')" @click="handleExport">
        <img :src="downloadIcon" alt="Export" />
      </button>
      <button v-if="showColumns" class="icon-btn" type="button" :title="$t('common.column_chooser')" @click="handleColumns">
        <img :src="columnIcon" alt="Columns" />
      </button>
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script>
import DxDropDownButton from "devextreme-vue/drop-down-button";

export default {
  name: "GridPanelHeader",
  props: {
    title: {
      type: String,
      default: "",
    },
    visible: {
      type: Boolean,
      default: true,
    },
    showColumns: {
      type: Boolean,
      default: true,
    },
    showExport: {
      type: Boolean,
      default: true,
    },
    showRefresh: {
      type: [Boolean, Function],
      default: false,
    },
    showAdd: {
      type: Boolean,
      default: true,
    },
    showFilter: {
      type: Boolean,
      default: false,
    },
    noBottomMargin: {
      type: Boolean,
      default: false,
    },
    gridRef: {
      type: Object,
      default: null,
    },
    downloadOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      plusIcon: require("@/assets/images/icons/plus.svg"),
      columnIcon: require("@/assets/images/icons/columns.svg"),
      downloadIcon: require("@/assets/images/icons/download.svg"),
      refreshIcon: require("@/assets/images/icons/refresh_map_green.svg"),
      filterIcon: require("@/assets/images/desktop/icons/brand-green/filter.svg"),
    };
  },
  components: {
    DxDropDownButton,
  },
  computed: {
    hasActionsSlot() {
      return !!this.$slots.actions;
    },
    hasTitle() {
      return typeof this.title === "string" && this.title.trim().length > 0;
    },
    hasDownloadOptions() {
      return Array.isArray(this.downloadOptions) && this.downloadOptions.length > 0;
    },
  },
  methods: {
    handleAdd() {
      if (this.$listeners && this.$listeners.add) {
        this.$emit("add");
        return;
      }

      const instance = this.gridRef && this.gridRef.instance ? this.gridRef.instance : null;
      if (instance) {
        //need to check if grid has insert method before call add
        if (typeof instance.addRow === "function") {
          instance.addRow();
        } else {
          console.warn("Grid instance does not have addRow method");
        }
      }
    },
    handleFilter() {
      if (this.$listeners && this.$listeners.filter) {
        this.$emit("filter");
        return;
      }

      const parent = this.$parent;
      if (parent && typeof parent.openFilterPanel === "function") {
        parent.openFilterPanel();
        return;
      }

      if (parent && Object.prototype.hasOwnProperty.call(parent, "filterPanelVisible")) {
        parent.filterPanelVisible = true;
      }
    },
    handleExport() {
      if (this.$listeners && this.$listeners.export) {
        this.$emit("export");
        return;
      }
      const instance = this.gridRef && this.gridRef.instance ? this.gridRef.instance : null;
      const parentExport = this.$parent && typeof this.$parent.onExporting === "function" ? this.$parent.onExporting : null;
      if (instance && parentExport) {
        parentExport({ component: instance, format: "xlsx" });
        return;
      }
      if (instance && typeof instance.exportToExcel === "function") {
        instance.exportToExcel(false);
      }
    },
    handleExportOptionClick(e) {
      const selectedOption = e?.itemData ?? null;
      if (!selectedOption) {
        return;
      }

      if (this.$listeners && this.$listeners["export-option-selected"]) {
        this.$emit("export-option-selected", selectedOption);
        return;
      }

      if (selectedOption.id === "excel_all") {
        this.handleExport();
      }
    },
    handleRefresh() {
      try {
        if (typeof this.showRefresh === "function") {
          this.showRefresh();
          return;
        }

        if (this.$listeners && this.$listeners.refresh) {
          this.$emit("refresh");
          return;
        }

        const instance = this.gridRef && this.gridRef.instance ? this.gridRef.instance : null;
        if (instance && typeof instance.refresh === "function") {
          instance.refresh();
        } else if (instance) {
          instance.getDataSource()?.reload?.();
        }
      } catch (error) {
        console.error("Error refreshing the grid:", error);
      }
    },
    handleColumns() {
      if (this.$listeners && this.$listeners.columns) {
        this.$emit("columns");
        return;
      }
      const instance = this.gridRef && this.gridRef.instance ? this.gridRef.instance : null;
      if (instance && typeof instance.showColumnChooser === "function") {
        instance.showColumnChooser();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/global/colors.scss";

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  margin-top: 0px;
  background: transparent;
  padding: 0;
  padding-right: 8px;
  padding-left: 4px;
  border: none;
}

.panel-header.no-title {
  padding-left: 0;
}

.panel-header.no-bottom-margin {
  margin-bottom: 4px;
}

.panel-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: $gray-90;
  padding-right: 8px;
  font-family: "Mulish", sans-serif;
}

.panel-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-left.no-title {
  gap: 0;
}

.panel-actions {
  display: flex;
  gap: 16px;
  align-items: center;
  padding-left: 4px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background: transparent;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  //transition: transform 0.1s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.icon-btn img {
  width: 16px;
  height: 16px;
}

.icon-btn:hover:not(:disabled),
.icon-btn:focus-visible:not(:disabled) {
  //transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
  border-color: $gray-30;
  outline: none;
}

.icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.icon-btn.primary {
  background: $brand;
  border-color: $brand;
  //box-shadow: 0 12px 24px rgba(26, 143, 61, 0.25);
}

.icon-btn.primary img {
  filter: brightness(0) invert(1);
}

.filter-btn {
  background: #fff;
  border: 1px solid $gray-20;
}

.panel-actions.right {
  padding-top: 24px;
}

.panel-actions.right.has-actions-slot {
  padding-top: 0;
}

.back-button {
  width: 34px;
  height: 34px;
  margin-right: 20px;
  border-radius: 50%;
}
</style>
